
import type { Track } from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { TrackService } from '@/services/track.service';
import { Image } from 'react-bootstrap';

interface TrackListProps {
    playlistId: string;
    tracks: Track[];
    onTrackAction: () => void; // Callback to refresh or notify parent
    isOwner: boolean;
}

export function TrackList({ playlistId, tracks, onTrackAction, isOwner }: TrackListProps) {
    const { user } = useAuth();

    if (!user) return null;

    const handleVote = async (trackId: string, newVote: 1 | -1 | 0) => {
        try {
            await TrackService.voteTrack(playlistId, trackId, user.id, newVote);
            onTrackAction();
        } catch (err) {
            console.error('Vote failed', err);
        }
    };

    const handleRemove = async (trackId: string) => {
        if (!confirm('Remove this track from the playlist?')) return;
        try {
            await TrackService.removeTrack(playlistId, trackId);
            onTrackAction();
        } catch (err) {
            console.error('Remove failed', err);
        }
    };

    // Sort tracks by score (desc), then addedAt (asc)
    const sortedTracks = [...tracks].sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        // Handle Date/Timestamp difference
        const dateA = a.addedAt instanceof Date ? a.addedAt : (a.addedAt as any).toDate();
        const dateB = b.addedAt instanceof Date ? b.addedAt : (b.addedAt as any).toDate();
        return dateA.getTime() - dateB.getTime();
    });

    return (
        <div className="list-group list-group-flush">
            {sortedTracks.map((track, index) => {
                const userVote = track.votes[user.id] || 0;

                return (
                    <div key={track.id} className="list-group-item d-flex align-items-center gap-3 py-3 border-0 border-bottom">
                        <div className="text-muted fw-bold" style={{ width: 24 }}>{index + 1}</div>

                        <Image src={track.thumbnail} rounded style={{ width: 80, height: 60, objectFit: 'cover' }} />

                        <div className="flex-grow-1 overflow-hidden">
                            <h6 className="mb-0 text-truncate" title={track.title}>{track.title}</h6>
                            <small className="text-muted">{track.artist}</small>
                            <div className="small text-muted mt-1">
                                Added by {track.addedBy === user.id ? 'You' : 'User'}
                            </div>
                        </div>

                        {/* Voting Controls */}
                        <div className="d-flex flex-column align-items-center me-3">
                            <button
                                className={`btn btn-sm btn-link p-0 text-decoration-none ${userVote === 1 ? 'text-success' : 'text-secondary'}`}
                                onClick={() => handleVote(track.id, userVote === 1 ? 0 : 1)}
                            >
                                <i className={`bi bi-caret-up-fill fs-5`}></i>
                            </button>

                            <span className={`fw-bold ${track.score > 0 ? 'text-success' : track.score < 0 ? 'text-danger' : 'text-muted'}`}>
                                {track.score}
                            </span>

                            <button
                                className={`btn btn-sm btn-link p-0 text-decoration-none ${userVote === -1 ? 'text-danger' : 'text-secondary'}`}
                                onClick={() => handleVote(track.id, userVote === -1 ? 0 : -1)}
                            >
                                <i className={`bi bi-caret-down-fill fs-5`}></i>
                            </button>
                        </div>

                        {/* Actions */}
                        {(isOwner || track.addedBy === user.id) && (
                            <button
                                className="btn btn-outline-danger btn-sm border-0"
                                onClick={() => handleRemove(track.id)}
                                title="Remove Track"
                            >
                                <i className="bi bi-trash"></i>
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
